// Facet-style equivalent of FacetsPanel, but for the document index (Dokumentenfilter) - moved
// out of DocumentSearchResults' own inline Select/Popover into the shared filter drawer for visual
// consistency with the product facets (same Filter trigger/badge in Toolbar, same drawer).
//
// Entries come from engine.computeDocumentFacets: value lists from the base set, counts smart-
// grayed per group (own filter excluded - see that function's doc comment and doka-pih principle
// 3). Rendering mirrors FacetsPanel's value-list convention exactly (shared building blocks in
// FacetGroup.tsx): collapsible group with bold heading, right-aligned grey count per value,
// long lists capped behind "N weitere anzeigen", checkbox/radio disabled at count 0 - grayed,
// never hidden, so the user sees WHY a combination yields nothing instead of options silently
// vanishing (the historic SPA bug).

import { Checkbox, Divider, Radio, Stack } from '@mantine/core';

import { humanizeDocType, languageLabel, type DocumentFacetEntry } from '../engine';
import { useCatalogLabels } from '../labels';
import { CountedLabel, FACET_ROW_STYLES, FacetGroup, FacetValueList } from './FacetGroup';

import type { ReactNode } from 'react';

interface DocumentFacetsPanelProps {
    /** Languages present in the base document set (search/node applied, facet filters not),
     * counts computed against the active docType/edition filters - see computeDocumentFacets. */
    languages: DocumentFacetEntry[];
    /** Doc type codes, counts computed against the active language/edition filters. */
    docTypes: DocumentFacetEntry[];
    /** Edition dates ("MM/YYYY", newest first), counts against language/docType filters. */
    editions: DocumentFacetEntry[];
    language: string | null;
    selectedDocTypes: string[];
    selectedEditions: string[];
    locale: string;
    onLanguageChange: (value: string | null) => void;
    onDocTypesChange: (value: string[]) => void;
    onEditionsChange: (value: string[]) => void;
}

export const DocumentFacetsPanel = ({
    languages,
    docTypes,
    editions,
    language,
    selectedDocTypes,
    selectedEditions,
    locale,
    onLanguageChange,
    onDocTypesChange,
    onEditionsChange,
}: DocumentFacetsPanelProps) => {
    const { L } = useCatalogLabels();

    // Same convention as the sibling DocumentFilterBar: nothing to render once there's only
    // one (or zero) value per facet - CatalogPage's own drawer-close effect already prevents this
    // from being reachable in normal use, but this stays as a defensive backstop.
    if (languages.length <= 1 && docTypes.length <= 1 && editions.length <= 1) {
        return null;
    }

    // A zero count disables the control - except when the value is currently SELECTED, which must
    // stay operable so the user can always deselect their way out of a zero-result combination.
    const sections: { key: string; title: string; summary?: string; body: ReactNode }[] = [];

    if (languages.length > 1) {
        sections.push({
            key: 'doc:language',
            title: L.documentLanguage,
            summary: language ? languageLabel(language, locale) : undefined,
            body: (
                <Radio.Group value={language ?? ''} onChange={(value) => onLanguageChange(value || null)}>
                    <Stack gap={8}>
                        <Radio label={L.allLanguages} styles={FACET_ROW_STYLES} value="" />
                        <FacetValueList
                            entries={languages}
                            labelOf={(entry) => languageLabel(entry.value, locale)}
                            renderEntry={(entry) => (
                                <Radio
                                    key={entry.value}
                                    disabled={entry.count === 0 && language !== entry.value}
                                    label={
                                        <CountedLabel count={entry.count} text={languageLabel(entry.value, locale)} />
                                    }
                                    styles={FACET_ROW_STYLES}
                                    value={entry.value}
                                />
                            )}
                            selected={language ? [language] : []}
                        />
                    </Stack>
                </Radio.Group>
            ),
        });
    }

    if (docTypes.length > 1) {
        sections.push({
            key: 'doc:docType',
            title: L.documentCategory,
            summary: selectedDocTypes.map(humanizeDocType).join(', ') || undefined,
            body: (
                <Checkbox.Group value={selectedDocTypes} onChange={onDocTypesChange}>
                    <FacetValueList
                        entries={docTypes}
                        labelOf={(entry) => humanizeDocType(entry.value)}
                        renderEntry={(entry) => (
                            <Checkbox
                                key={entry.value}
                                disabled={entry.count === 0 && !selectedDocTypes.includes(entry.value)}
                                label={<CountedLabel count={entry.count} text={humanizeDocType(entry.value)} />}
                                styles={FACET_ROW_STYLES}
                                value={entry.value}
                            />
                        )}
                        selected={selectedDocTypes}
                    />
                </Checkbox.Group>
            ),
        });
    }

    if (editions.length > 1) {
        sections.push({
            key: 'doc:edition',
            title: L.documentEdition,
            summary: selectedEditions.join(', ') || undefined,
            body: (
                <Checkbox.Group value={selectedEditions} onChange={onEditionsChange}>
                    <FacetValueList
                        entries={editions}
                        labelOf={(entry) => entry.value}
                        renderEntry={(entry) => (
                            <Checkbox
                                key={entry.value}
                                disabled={entry.count === 0 && !selectedEditions.includes(entry.value)}
                                label={<CountedLabel count={entry.count} text={entry.value} />}
                                styles={FACET_ROW_STYLES}
                                value={entry.value}
                            />
                        )}
                        selected={selectedEditions}
                    />
                </Checkbox.Group>
            ),
        });
    }

    return (
        <Stack gap="md">
            {sections.map((section, index) => (
                <div key={section.key}>
                    {index > 0 && <Divider color="var(--pih-border)" mb="md" />}
                    <FacetGroup groupKey={section.key} summary={section.summary} title={section.title}>
                        {section.body}
                    </FacetGroup>
                </div>
            ))}
        </Stack>
    );
};
